import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IdCard,
  User,
  Target,
  Calendar,
  ThumbsUp,
  FileText,
  Award,
  Receipt,
  CircleSmall,
  Loader2,
} from 'lucide-react';

interface QuickLink {
  label: string;
  icon: React.ReactNode;
  route: string;
  requiredPermission?: string;
  hideOnMobile?: boolean;
}

interface QuickLinksProps {
  onMemberCardClick?: () => void;
  loadingMemberCard?: boolean;
  onInvoicesClick?: () => void;
  loadingInvoices?: boolean;
}

const QuickLinks: React.FC<QuickLinksProps> = ({
  onMemberCardClick,
  loadingMemberCard = false,
  onInvoicesClick,
  loadingInvoices = false,
}) => {
  const navigate = useNavigate();

  const quickLinks: QuickLink[] = [
    {
      label: 'My Membership Card',
      icon: <IdCard className="w-8 h-8 mb-3" />,
      route: '#',
      requiredPermission: 'Main.Documents.View',
    },
    {
      label: 'My Profile',
      icon: <User className="w-8 h-8 mb-3" />,
      route: '/admin/member-profile',
      requiredPermission: 'Main.Profile.View',
      hideOnMobile: true,
    },
    {
      label: 'Firearms',
      icon: <CircleSmall className="w-8 h-8 mb-3" />,
      route: '/admin/memberfirearms',
      requiredPermission: 'Manage.MemberFirearm.View',
    },
    {
      label: 'Competencies',
      icon: <CircleSmall className="w-8 h-8 mb-3" />,
      route: '/admin/membercompetencies',
      requiredPermission: 'Manage.MemberCompetency.View',
    },
    {
      label: 'Scores',
      icon: <Target className="w-8 h-8 mb-3" />,
      route: '/admin/scores',
      requiredPermission: 'Manage.Score.View',
    },
    {
      label: 'Activities',
      icon: <Calendar className="w-8 h-8 mb-3" />,
      route: '/admin/activities',
      requiredPermission: 'Manage.Activity.View',
    },
    {
      label: 'Endorsements',
      icon: <ThumbsUp className="w-8 h-8 mb-3" />,
      route: '/admin/memberendorsements',
      requiredPermission: 'Manage.MemberEndorsement.View',
    },
    {
      label: 'Documents',
      icon: <FileText className="w-8 h-8 mb-3" />,
      route: '/admin/documents',
      requiredPermission: 'Main.Documents.View',
    },
    {
      label: 'Dedicated Status',
      icon: <Award className="w-8 h-8 mb-3" />,
      route: '/admin/dedicatedstatus',
      requiredPermission: 'Manage.Course.View',
      hideOnMobile: true,
    },
    {
      label: 'Invoices',
      icon: <Receipt className="w-8 h-8 mb-3" />,
      route: '#',
      requiredPermission: 'Manage.Invoice.View',
    },
  ];

  return (
    <div className="mb-8">
      <h2 className="text-sm font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-4">
        Quick Links
      </h2>

      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {quickLinks.map((link, index) => {
          const isInvoicesLink = link.label === 'Invoices';
          const isMemberCardLink = link.label === 'My Membership Card';
          const isLoadingThis =
            (isMemberCardLink && loadingMemberCard) ||
            (isInvoicesLink && loadingInvoices);
          const isDisabled = isLoadingThis;

          return (
            <button
              key={index}
              onClick={() => {
                if (isMemberCardLink && onMemberCardClick) {
                  onMemberCardClick();
                } else if (isInvoicesLink && onInvoicesClick) {
                  onInvoicesClick();
                } else {
                  navigate(link.route);
                }
              }}
              disabled={isDisabled}
              className={`bg-gradient-to-br from-gray-800 to-gray-900 dark:from-slate-800 dark:to-slate-950 
                     text-white rounded-lg p-6 shadow-md hover:shadow-xl 
                     transition-all duration-300 transform hover:scale-105 
                     flex flex-col items-center justify-center text-center min-h-[120px] w-full
                     focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2
                     disabled:opacity-70 disabled:hover:scale-100
                     ${link.hideOnMobile ? 'hidden md:flex' : ''}`}
            >
              {isLoadingThis ? (
                <Loader2 className="w-8 h-8 mb-3 animate-spin" />
              ) : (
                link.icon
              )}
              <span className="text-sm font-medium leading-tight">{link.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default QuickLinks;